import { useMemo } from 'react';
import type { ActId, FilterState } from '@/types';
import { mandateHashFor } from '@/hooks/useHashRoute';
import instrumentsData from '@/data/instruments/index.json';
import styles from './MandateMatrix.module.css';

const INSTRUMENTS = instrumentsData as Record<string, any>;

const ACTS: { id: ActId; label: string; color: string }[] = [
  { id: 'amld', label: 'AMLD', color: '#1D4ED8' },
  { id: 'amlr', label: 'AMLR', color: '#0D9488' },
  { id: 'amlar', label: 'AMLAR', color: '#7C3AED' },
  { id: 'tfr', label: 'TFR', color: '#C2410C' },
];

// Status display order (top -> bottom) and colours, matching the rest of the tool.
const STATUS_ORDER: { key: string; label: string; color: string; abbr: string }[] = [
  { key: 'adopted', label: 'Adopted', color: '#6D28D9', abbr: 'A' },
  { key: 'final_report', label: 'Final report', color: '#15803D', abbr: 'FR' },
  { key: 'consultation_closed', label: 'Consultation closed', color: '#1D4ED8', abbr: 'CC' },
  { key: 'consultation_open', label: 'Consultation open', color: '#B45309', abbr: 'OC' },
  { key: 'planned', label: 'Planned', color: '#475569', abbr: 'P' },
];
const ORDER_IDX: Record<string, number> = Object.fromEntries(STATUS_ORDER.map((s, i) => [s.key, i]));

// Chronological key within a status group: statutory date, then deadline, then legal basis.
function chronoKey(m: any): string {
  const s = m.schedule || {};
  return `${s.statutory?.date || ''}|${m.deadline || ''}|${m.legalBasis || ''}`;
}

// The date shown in the card badge (top-right), chosen by status:
// adopted/final_report -> the adoption/finalisation quarter (stored as keyQuarter);
// planned -> the expected consultation quarter; open/closed -> the expected final-draft quarter.
function badgeQuarter(m: any): string | null {
  if (m.status === 'adopted' || m.status === 'final_report') return m.keyQuarter || null;
  if (m.status === 'planned') return m.schedule?.consultation?.quarter || m.deadline || null;
  return m.deadline || m.schedule?.finalDraft?.quarter || null;
}

export function MandateMatrix({ visibleActs, filters }: { visibleActs: Set<ActId>; filters: FilterState }) {
  const groups = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    const all = Object.values(INSTRUMENTS)
      .filter((m: any) => {
        // Type filter (RTS/ITS/GL)
        if (filters.types.length > 0 && !filters.types.includes(m.type)) return false;
        // Status filter
        if (filters.statuses.length > 0 && !filters.statuses.includes(m.status)) return false;
        // Query: match legal basis, title or type
        if (q && !(`${m.type} ${m.legalBasis} ${m.title}`.toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) => {
        const oa = ORDER_IDX[a.status] ?? 99, ob = ORDER_IDX[b.status] ?? 99;
        if (oa !== ob) return oa - ob;
        return chronoKey(a).localeCompare(chronoKey(b));
      });
    const byStatus: Record<string, any[]> = {};
    for (const m of all) (byStatus[m.status] = byStatus[m.status] || []).push(m);
    return STATUS_ORDER
      .filter(s => byStatus[s.key]?.length)
      .map(s => ({ ...s, items: byStatus[s.key] }));
  }, [filters]);

  const cols = ACTS.filter(a => visibleActs.has(a.id));

  const openMandate = (id: string) => window.open(mandateHashFor(id), '_blank', 'noopener');

  if (!groups.length) {
    return <div className={styles.wrap}><div className={styles.empty}>No mandates match the current filters.</div></div>;
  }

  return (
    <div className={styles.wrap}>
      <table className={styles.matrix}>
        <thead>
          <tr>
            {cols.map(a => (
              <th key={a.id} style={{ borderBottomColor: a.color, color: a.color, width: `${100 / cols.length}%` }}>{a.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(g => (
            <>
              <tr key={`st-${g.key}`}>
                <td className={styles.statusRow} colSpan={cols.length}>
                  <span className={styles.statusBadge} style={{ color: g.color, borderColor: g.color }}>{g.abbr}</span>
                  {g.label}<span className={styles.statusCount}>{g.items.length}</span>
                </td>
              </tr>
              <tr key={`data-${g.key}`} className={styles.dataRow}>
                {cols.map(a => {
                  const its = g.items.filter((m: any) => m.actId === a.id);
                  return (
                    <td key={a.id} className={its.length ? styles.cell : styles.emptyCell}>
                      {its.map((m: any) => (
                        <button key={m.id} className={styles.card} style={{ borderLeftColor: a.color }}
                          onClick={() => openMandate(m.id)} title={`Open ${m.legalBasis} — ${m.title}`}>
                          <span className={styles.cardTop}>
                            <span className={styles.cardType} style={{ color: a.color }}>{m.type}</span>
                            {badgeQuarter(m) && <span className={styles.cardDate}>{badgeQuarter(m)}</span>}
                          </span>
                          <span className={styles.cardLb}>{m.legalBasis}</span>
                          <span className={styles.cardTitle}>{m.title}</span>
                        </button>
                      ))}
                    </td>
                  );
                })}
              </tr>
            </>
          ))}
        </tbody>
      </table>
      <div className={styles.footnote}>Click a mandate to open its reader in a new tab. Grouped by status; chronological within each group.</div>
    </div>
  );
}
